/**
 * Nutri-Leaf unified animation controller.
 *
 * One scroll-reveal observer handles both Dawn .scroll-trigger elements
 * and Nutri-Leaf .nl-reveal elements — same transition, same timing.
 * One offscreen observer pauses .nl-motion-zone containers.
 * Motion toggle respects WCAG 2.2.2 (user pause control).
 */

// JS is running — nl-reveal elements may be hidden until they intersect.
// Without this marker, .nl-reveal CSS keeps content visible (no-JS safe).
document.documentElement.classList.add('nl-js');

const SCROLL_TRIGGER = 'scroll-trigger';
const SCROLL_OFFSCREEN = 'scroll-trigger--offscreen';
const SCROLL_CANCEL = 'scroll-trigger--cancel';
const SCROLL_ZOOM = 'animate--zoom-in';
const NL_REVEAL = 'nl-reveal';
const NL_REVEAL_VISIBLE = 'nl-reveal--visible';
const NL_MOTION_ZONE = 'nl-motion-zone';
const NL_OFFSCREEN = 'data-nl-offscreen';
const NL_MOTION_ATTR = 'data-motion';
const NL_MOTION_PAUSED = 'paused';

/* ── Shared page motion system (FP-16, 2026-08-23) ───────────── */

const NL_MOTION_VARIANT = 'data-nl-motion';
const NL_MOTION_VISIBLE = 'is-visible';

// Variant rank by density setting: low = key blocks only,
// medium = + headings/cards, high = everything.
const NL_MOTION_RANK = {
  'fade-up': 1,
  'image-reveal': 1,
  'foreground': 1,
  'mask-line': 2,
  'card-cascade': 2,
  'chapter-accent': 3,
  'word-stagger': 3,
};
const NL_DENSITY_RANK = { low: 1, medium: 2, high: 3 };

function nlMotionEnabled() {
  return document.body.dataset.nlMotionEnabled !== 'false';
}

function nlDensityRank() {
  const value = document.body.dataset.nlMotionDensity || 'medium';
  return NL_DENSITY_RANK[value] || 2;
}

function nlSpeedScale() {
  const value = document.body.dataset.nlMotionSpeed || 'normal';
  return value === 'quick' ? 0.7 : value === 'relaxed' ? 1.4 : 1;
}

function nlRevealMotionTargets(rootEl, cascadeCounter) {
  // Returns targets to observe, revealing out-of-density / disabled
  // targets immediately. cascadeCounter is an object so card-cascade
  // delays accumulate across root scopes (theme editor reloads).
  const targets = [];
  rootEl.querySelectorAll('[' + NL_MOTION_VARIANT + ']').forEach((el) => {
    if (!nlMotionEnabled() || (NL_MOTION_RANK[el.getAttribute(NL_MOTION_VARIANT)] || 1) > nlDensityRank()) {
      el.classList.add(NL_MOTION_VISIBLE);
      return;
    }
    if (el.getAttribute(NL_MOTION_VARIANT) === 'card-cascade') {
      el.style.setProperty('--nl-cascade-delay', cascadeCounter.value * 80 * nlSpeedScale() + 'ms');
      cascadeCounter.value += 1;
    }
    targets.push(el);
  });
  return targets;
}

/* word-stagger: tokenize only plain-text display headings, after
   translation has rendered. aria-label keeps the normalized original
   phrase; word wrappers are aria-hidden. Skip headings with links or
   inline markup. Delays capped so long translated headings do not
   take several seconds. */
function initWordStagger(rootEl = document) {
  if (!nlMotionEnabled()) return;
  rootEl.querySelectorAll('[' + NL_MOTION_VARIANT + '="word-stagger"]').forEach((heading) => {
    if (heading._nlWordsBuilt) return;
    heading._nlWordsBuilt = true;
    if (heading.querySelector('a, img, strong, em, mark, code, span, svg, button')) return;
    const normalized = (heading.textContent || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return;
    heading.setAttribute('aria-label', normalized);
    const words = normalized.split(' ');
    const step = 65 * nlSpeedScale();
    const cap = 750 * nlSpeedScale();
    const fragment = document.createDocumentFragment();
    words.forEach((word, index) => {
      const span = document.createElement('span');
      span.className = 'nl-motion-word';
      span.setAttribute('aria-hidden', 'true');
      span.style.setProperty('--nl-word-delay', Math.min(index * step, cap) + 'ms');
      span.textContent = word;
      fragment.appendChild(span);
      fragment.appendChild(document.createTextNode(' '));
    });
    heading.replaceChildren(fragment);
  });
}

/* ── Unified scroll reveal ─────────────────────────────────── */

function onRevealIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    if (!entry.isIntersecting) return;
    const el = entry.target;

    if (el.classList.contains(SCROLL_OFFSCREEN)) {
      el.classList.remove(SCROLL_OFFSCREEN);
      if (el.hasAttribute('data-cascade')) {
        el.style.setProperty('--animation-order', index);
        // Stagger must come from JS inline style: calc() inside
        // animation-delay is a Shopify-processor-prohibited pattern that
        // would drop the rule and strand content at opacity 0.01 (RV-02).
        el.style.animationDelay = index * 75 + 'ms';
      }
    }

    if (el.classList.contains(NL_REVEAL)) {
      const delay = parseInt(el.dataset.nlRevealDelay, 10) || 0;
      el.style.setProperty('--nl-reveal-delay', delay + 'ms');
      el.classList.add(NL_REVEAL_VISIBLE);
    }

    if (el.hasAttribute(NL_MOTION_VARIANT)) {
      el.classList.add(NL_MOTION_VISIBLE);
    }

    observer.unobserve(el);
  });
}

function initScrollReveal(rootEl = document, isDesignMode = false) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    rootEl.querySelectorAll('.' + NL_REVEAL).forEach(el => el.classList.add(NL_REVEAL_VISIBLE));
    rootEl.querySelectorAll('[' + NL_MOTION_VARIANT + ']').forEach(el => el.classList.add(NL_MOTION_VISIBLE));
    rootEl.querySelectorAll('.' + SCROLL_TRIGGER).forEach(el => {
      el.classList.remove(SCROLL_OFFSCREEN);
      if (el.hasAttribute('data-cascade'))
        el.style.setProperty('--animation-order', 0);
    });
    return;
  }

  if (isDesignMode) {
    rootEl.querySelectorAll('.' + SCROLL_TRIGGER).forEach(el => el.classList.add('scroll-trigger--design-mode'));
    rootEl.querySelectorAll('[' + NL_MOTION_VARIANT + ']').forEach(el => el.classList.add(NL_MOTION_VISIBLE));
    return;
  }

  const cascadeCounter = { value: 0 };
  const motionTargets = nlRevealMotionTargets(rootEl, cascadeCounter);
  const elements = rootEl.querySelectorAll('.' + SCROLL_TRIGGER + ', .' + NL_REVEAL);
  if (elements.length === 0 && motionTargets.length === 0) return;

  const observer = new IntersectionObserver(onRevealIntersection, {
    rootMargin: '0px 0px -30px 0px',
    threshold: 0.1,
  });
  elements.forEach(el => observer.observe(el));
  motionTargets.forEach(el => observer.observe(el));
}

/* ── Zoom animation (Dawn pattern, kept for compatibility) ──── */

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) return 0;
  if (elementPositionY + elementHeight < scrollY) return 100;

  const distance = scrollY + viewportHeight - elementPositionY;
  return Math.round(distance / ((viewportHeight + elementHeight) / 100));
}

function throttle(fn, wait = 16) {
  let time = Date.now();
  return function (...args) {
    if (Date.now() - time < wait) return;
    time = Date.now();
    return fn(...args);
  };
}

function initScrollZoom() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const elements = Array.from(document.getElementsByClassName(SCROLL_ZOOM));
  if (elements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  elements.forEach(element => {
    let visible = false;
    new IntersectionObserver(entries => {
      entries.forEach(e => { visible = e.isIntersecting; });
    }).observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    window.addEventListener('scroll', throttle(() => {
      if (!visible) return;
      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    }), { passive: true });
  });
}

/* ── Offscreen motion pause ─────────────────────────────────── */

function initOffscreenPause(rootEl = document) {
  const zones = rootEl.querySelectorAll('.' + NL_MOTION_ZONE);
  if (zones.length === 0) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.removeAttribute(NL_OFFSCREEN);
      } else {
        entry.target.setAttribute(NL_OFFSCREEN, 'true');
      }
    });
  }, { rootMargin: '100px 0px 100px 0px', threshold: 0.05 });

  zones.forEach(el => observer.observe(el));
}

/* ── Motion toggle (WCAG 2.2.2) ────────────────────────────── */

function nlIsMotionPaused() {
  return document.documentElement.getAttribute(NL_MOTION_ATTR) === NL_MOTION_PAUSED;
}

function nlSetMotionPaused(paused) {
  if (paused) {
    document.documentElement.setAttribute(NL_MOTION_ATTR, NL_MOTION_PAUSED);
  } else {
    document.documentElement.removeAttribute(NL_MOTION_ATTR);
  }
  updateMotionLabels();
  document.dispatchEvent(new CustomEvent('nl:motionchange', { detail: { paused } }));
}

function updateMotionLabels() {
  const paused = nlIsMotionPaused();
  document.querySelectorAll('[data-nl-motion-toggle]').forEach(btn => {
    const label = btn.querySelector('[data-nl-motion-label]');
    if (label) {
      label.textContent = paused
        ? (btn.getAttribute('data-nl-motion-play-label') || 'Play motion')
        : (btn.getAttribute('data-nl-motion-pause-label') || 'Pause motion');
    }
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    btn.setAttribute('aria-label', paused
      ? (btn.getAttribute('data-nl-motion-play-label') || 'Play motion')
      : (btn.getAttribute('data-nl-motion-pause-label') || 'Pause motion'));
  });
}

window.nlToggleMotion = function () {
  nlSetMotionPaused(!nlIsMotionPaused());
};

function initMotionToggles() {
  document.querySelectorAll('[data-nl-motion-toggle]').forEach(btn => {
    if (btn._nlMotionBound) return;
    btn._nlMotionBound = true;
    btn.addEventListener('click', () => nlSetMotionPaused(!nlIsMotionPaused()));
  });
  updateMotionLabels();
}

/* ── Boot ──────────────────────────────────────────────────── */

window.addEventListener('DOMContentLoaded', () => {
  initScrollReveal();
  initWordStagger();
  initScrollZoom();
  initOffscreenPause();
  initMotionToggles();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', event => {
    initScrollReveal(event.target);
    initWordStagger(event.target);
    initOffscreenPause(event.target);
    initMotionToggles(event.target);
  });
  document.addEventListener('shopify:section:select', event => {
    initScrollReveal(event.target, true);
  });
}
